import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import { Loading } from "./components/kit";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Home from "./pages/Home";
import Training from "./pages/Training";
import CoursePlayer from "./pages/CoursePlayer";
import Records from "./pages/Records";
import Sds from "./pages/Sds";
import SdsDetail from "./pages/SdsDetail";
import Concern from "./pages/Concern";
import ReportDetail from "./pages/ReportDetail";
import HelpPage from "./pages/HelpPage";
import Policies from "./pages/Policies";
import Performance from "./pages/Performance";
import ReviewPage from "./pages/ReviewPage";
import TemplateEditor from "./pages/TemplateEditor";
import OrgChart from "./pages/OrgChart";
import Links from "./pages/Links";
import Team from "./pages/Team";
import Person from "./pages/Person";
import Reviews from "./pages/Reviews";
import Admin from "./pages/admin/Admin";
import CourseEditor from "./pages/admin/CourseEditor";
import WalkthroughReport from "./pages/WalkthroughReport";
import CoursePreview from "./pages/admin/CoursePreview";
import PreviewFrame from "./pages/admin/PreviewFrame";

function Protected({ children, when = () => true }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!when(user)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function FrameOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user || !editor(user)) return <Navigate to="/" replace />;
  return children;
}

const mgr = (u) => u.is_manager || u.is_superadmin;
const admin = (u) => u.is_superadmin;
const editor = (u) => u.is_superadmin || (u.permissions || []).includes("course_editor");

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Home /></Protected>} />
      <Route path="/training" element={<Protected><Training /></Protected>} />
      <Route path="/training/:aid" element={<Protected><CoursePlayer /></Protected>} />
      <Route path="/walkthroughs/:aid" element={<Protected><WalkthroughReport /></Protected>} />
      <Route path="/records" element={<Protected><Records /></Protected>} />
      <Route path="/sds" element={<Protected><Sds /></Protected>} />
      <Route path="/sds/:sid" element={<Protected><SdsDetail /></Protected>} />
      <Route path="/concern" element={<Protected><Concern /></Protected>} />
      <Route path="/concern/reports/:rid" element={<Protected><ReportDetail /></Protected>} />
      <Route path="/concern/help" element={<Protected><HelpPage /></Protected>} />
      <Route path="/policies" element={<Protected><Policies /></Protected>} />
      <Route path="/documents" element={<DocsRedirect />} />
      <Route path="/performance" element={<Protected><Performance /></Protected>} />
      <Route path="/performance/templates/:tid" element={<Protected when={mgr}><TemplateEditor /></Protected>} />
      <Route path="/performance/:rid" element={<Protected><ReviewPage /></Protected>} />
      <Route path="/org-chart" element={<Protected><OrgChart /></Protected>} />
      <Route path="/links" element={<Protected><Links /></Protected>} />
      <Route path="/team" element={<Protected when={mgr}><Team /></Protected>} />
      <Route path="/team/:uid" element={<Protected when={mgr}><Person /></Protected>} />
      <Route path="/reviews" element={<Protected><Reviews /></Protected>} />
      <Route path="/reviews/safety/:rid" element={<Protected><ReportDetail /></Protected>} />
      <Route path="/admin" element={<Protected when={editor}><Admin /></Protected>} />
      <Route path="/admin/:tab" element={<Protected when={editor}><Admin /></Protected>} />
      <Route path="/admin/courses/:cid" element={<Protected when={editor}><CourseEditor /></Protected>} />
      <Route path="/admin/courses/:cid/preview" element={<Protected when={editor}><CoursePreview /></Protected>} />
      <Route path="/preview-frame/:cid" element={<FrameOnly><PreviewFrame /></FrameOnly>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function DocsRedirect() {
  const loc = useLocation();
  return <Navigate to={`/records?tab=documents${loc.hash}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
